import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, X, ArrowRight, Tag, Package } from 'lucide-react'
import { productsApi } from '../services/api'
import { useDebounce } from '../hooks/useDebounce'
import { useAnalytics } from '../hooks/useAnalytics'
import './SearchModal.css'

/**
 * SearchModal — Palette de recherche globale activée par ⌘K / Ctrl+K.
 *
 * Brancher dans App.jsx ou Navbar :
 *   const [searchOpen, setSearchOpen] = useState(false)
 *   <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} lang={lang} />
 *   <button onClick={() => setSearchOpen(true)}>⌘K</button>
 */
export default function SearchModal({ open, onClose, lang = 'fr' }) {
  const [query, setQuery]           = useState('')
  const [results, setResults]       = useState([])
  const [loading, setLoading]       = useState(false)
  const [selected, setSelected]     = useState(0)
  const inputRef                    = useRef(null)
  const navigate                    = useNavigate()
  const { trackSearch }             = useAnalytics()
  const debouncedQuery              = useDebounce(query, 300)

  /* ── Focus sur ouverture ── */
  useEffect(() => {
    if (open) {
      setQuery('')
      setResults([])
      setSelected(0)
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  /* ── Raccourci clavier global ⌘K / Ctrl+K ── */
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        if (open) onClose()
        // L'ouverture est gérée par le parent via le même raccourci
      }
      if (e.key === 'Escape' && open) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  /* ── Recherche avec debounce ── */
  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([])
      return
    }

    let cancelled = false
    setLoading(true)

    productsApi.suggest(debouncedQuery)
      .then(data => {
        if (!cancelled) {
          setResults(data.data ?? [])
          setSelected(0)
          if (debouncedQuery.length >= 3) trackSearch(debouncedQuery)
        }
      })
      .catch(() => { if (!cancelled) setResults([]) })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [debouncedQuery])

  /* ── Navigation clavier ── */
  const handleKey = useCallback((e) => {
    if (!results.length) return
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(s + 1, results.length - 1)) }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(s - 1, 0)) }
    if (e.key === 'Enter') {
      e.preventDefault()
      const r = results[selected]
      if (r) goToProduct(r)
    }
  }, [results, selected])

  const goToProduct = (product) => {
    navigate(`/produits/${product.slug}`)
    onClose()
  }

  const goToCatalogue = () => {
    if (query.trim()) navigate(`/catalogue?search=${encodeURIComponent(query.trim())}`)
    else navigate('/catalogue')
    onClose()
  }

  const name = (p) => lang === 'fr' ? (p.name_fr || p.name_en) : (p.name_en || p.name_fr)
  const price = (p) => {
    const v = p.flash_sale_price || p.price
    return new Intl.NumberFormat('fr-GN').format(v) + ' GNF'
  }

  if (!open) return null

  return (
    <div className="search-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-label="Recherche">
      <div className="search-modal" onClick={e => e.stopPropagation()} onKeyDown={handleKey}>

        {/* ── Input ── */}
        <div className="search-input-row">
          <Search size={18} className="search-icon" />
          <input
            ref={inputRef}
            className="search-input"
            type="text"
            placeholder={lang === 'fr' ? 'Rechercher un produit…' : 'Search a product…'}
            value={query}
            onChange={e => setQuery(e.target.value)}
            autoComplete="off"
          />
          {query && (
            <button className="search-clear" onClick={() => setQuery('')} aria-label="Effacer">
              <X size={16} />
            </button>
          )}
          <kbd className="search-esc">ESC</kbd>
        </div>

        {/* ── Résultats ── */}
        {query.length >= 2 && (
          <div className="search-results">
            {loading && (
              <div className="search-loading">
                {[1,2,3].map(i => (
                  <div key={i} className="search-result-skeleton">
                    <div className="skeleton-img" />
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div className="skeleton-line" style={{ width: '60%' }} />
                      <div className="skeleton-line" style={{ width: '30%', height: 10 }} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!loading && results.length === 0 && (
              <div className="search-empty">
                <Package size={32} strokeWidth={1.5} />
                <span>{lang === 'fr' ? 'Aucun résultat pour' : 'No results for'} « {query} »</span>
              </div>
            )}

            {!loading && results.length > 0 && (
              <ul className="search-list" role="listbox">
                {results.map((product, i) => (
                  <li
                    key={product.id}
                    className={`search-result-item ${i === selected ? 'search-result-active' : ''}`}
                    onClick={() => goToProduct(product)}
                    onMouseEnter={() => setSelected(i)}
                    role="option"
                    aria-selected={i === selected}
                  >
                    <div className="search-result-img">
                      {product.image_url
                        ? <img src={product.image_url} alt={name(product)} loading="lazy" />
                        : <Package size={20} strokeWidth={1.5} />
                      }
                    </div>
                    <div className="search-result-info">
                      <span className="search-result-name">{name(product)}</span>
                      <span className="search-result-cat">{product.category?.name_fr || ''}</span>
                    </div>
                    <div className="search-result-right">
                      <span className="search-result-price">{price(product)}</span>
                      {product.flash_sale_price && (
                        <span className="search-result-badge">Promo</span>
                      )}
                      <ArrowRight size={14} className="search-result-arrow" />
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* ── Voir tout ── */}
            {!loading && (
              <button className="search-see-all" onClick={goToCatalogue}>
                <Tag size={14} />
                {lang === 'fr'
                  ? `Voir tous les résultats pour « ${query} »`
                  : `See all results for "${query}"`}
                <ArrowRight size={14} />
              </button>
            )}
          </div>
        )}

        {/* ── État initial — suggestions rapides ── */}
        {query.length < 2 && (
          <div className="search-hints">
            <div className="search-hint-label">
              {lang === 'fr' ? 'Accès rapide' : 'Quick access'}
            </div>
            <div className="search-hint-links">
              {[
                { label: lang === 'fr' ? '🛋 Meubles' : '🛋 Furniture', cat: 'meubles' },
                { label: lang === 'fr' ? '📺 Électroménager' : '📺 Appliances', cat: 'electromenager' },
                { label: lang === 'fr' ? '🪴 Décoration' : '🪴 Decoration', cat: 'decoration' },
                { label: lang === 'fr' ? '✨ Nouveautés' : '✨ New arrivals', cat: null, path: '/catalogue?sort=newest' },
                { label: lang === 'fr' ? '🔥 Promotions' : '🔥 Sales', cat: null, path: '/catalogue?sale=1' },
              ].map(({ label, cat, path }) => (
                <button
                  key={label}
                  className="search-hint-chip"
                  onClick={() => {
                    navigate(path || `/catalogue?category=${cat}`)
                    onClose()
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="search-footer-hint">
              <span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span>
              <span><kbd>↵</kbd> ouvrir</span>
              <span><kbd>Esc</kbd> fermer</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
